/**
 * Formatacao PT-BR (RF-UX-094..099). Datas relativas curtas, absolutas no
 * detalhe, sempre em pt-BR e no fuso do dispositivo.
 */
const RELATIVE = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });
const TIME = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" });
const DATE = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" });
const DATE_YEAR = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
const WEEKDAY = new Intl.DateTimeFormat("pt-BR", { weekday: "short" });

/**
 * Quando a mensagem saiu no WhatsApp. `createdAt` (quando gravamos a linha) e
 * so fallback: no trafego ao vivo os dois batem, mas em historico importado
 * divergem por dias — usar `createdAt` punha a conversa inteira na mesma hora.
 * `externalTimestamp` vem como epoch em milissegundos numa string.
 */
export function messageAt(
  message: { externalTimestamp?: string | null; createdAt: string },
): string {
  const epoch = Number(message.externalTimestamp);
  if (Number.isFinite(epoch) && epoch > 0) return new Date(epoch).toISOString();
  return message.createdAt;
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return TIME.format(d);
}

export function formatDay(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Hoje";
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Ontem";
  return DATE.format(d);
}

/** "agora", "5 min", "14:32", "ontem", "12/09". */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (Math.abs(diffMin) < 1) return "agora";
  if (Math.abs(diffMin) < 60) return RELATIVE.format(-diffMin, "minute").replace("há ", "");
  const dToday = new Date();
  const sameDay = d.toDateString() === dToday.toDateString();
  if (sameDay) return TIME.format(d);
  const yesterday = new Date(dToday);
  yesterday.setDate(dToday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `ontem ${TIME.format(d)}`;
  return DATE.format(d);
}

/**
 * Horario da lista de conversas no padrao Telegram: hoje mostra a hora,
 * ontem "Ontem", a semana mostra o dia da semana e o resto a data.
 */
export function formatChatListTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return TIME.format(d);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Ontem";
  const startOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (diffDays > 0 && diffDays < 7) return WEEKDAY.format(d).replace(".", "");
  if (d.getFullYear() === now.getFullYear()) return DATE.format(d);
  return DATE_YEAR.format(d);
}

export function formatSla(iso: string | null | undefined): string {
  if (!iso) return "";
  const diffMin = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (diffMin <= 0) return `SLA estourou · ${Math.abs(diffMin)} min`;
  return `no prazo · ${diffMin} min`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

export function plural(n: number, singular: string, pluralForm: string): string {
  return n === 1 ? `1 ${singular}` : `${n} ${pluralForm}`;
}

/**
 * Cor de tinta automatica por luminancia relativa (RF-UX-084/110).
 * Escolhe entre branco e preto a opcao de MAIOR contraste: garante >=4.5:1
 * para qualquer cor de fundo (WCAG AA), inclusive os tons medios que o
 * limiar antigo (0.45) deixava passar com branco.
 */
export function readableInk(hex: string): string {
  const normalized = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return "#ffffff";
  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  const againstWhite = 1.05 / (luminance + 0.05);
  const againstBlack = (luminance + 0.05) / 0.05;
  return againstBlack >= againstWhite ? "#000000" : "#ffffff";
}

export function truncate(text: string, max = 80): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

const linear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);

function hexToRgb(hex: string): [number, number, number] | null {
  const normalized = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
}

function rgbToHex(rgb: [number, number, number]): string {
  return `#${rgb
    .map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0"))
    .join("")}`;
}

function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const la = 0.2126 * linear(a[0] / 255) + 0.7152 * linear(a[1] / 255) + 0.0722 * linear(a[2] / 255);
  const lb = 0.2126 * linear(b[0] / 255) + 0.7152 * linear(b[1] / 255) + 0.0722 * linear(b[2] / 255);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

function blendOver(fg: [number, number, number], bg: [number, number, number], alpha: number): [number, number, number] {
  return [0, 1, 2].map((i) => fg[i]! * alpha + bg[i]! * (1 - alpha)) as [number, number, number];
}

/**
 * Tinta legivel (WCAG AA) na MATIZ da etiqueta para chips de fundo suave.
 * O fundo do chip e a cor da etiqueta com `alpha` sobre a superficie do tema,
 * entao a busca leva o fundo real em conta e escurece (dia) ou clareia (noite)
 * a cor original ate atingir 4.6:1 (margem sobre os 4.5:1 do AA para
 * divergencias de arredondamento entre motores, ex. axe) — mantendo o
 * reconhecimento pela cor (RF-UX-088).
 */
export function tagInk(hex: string, theme: "light" | "dark", alpha = 0.14): string {
  const base = hexToRgb(hex);
  if (!base) return theme === "dark" ? "#ffffff" : "#000000";
  const surface: [number, number, number] = theme === "dark" ? [0x21, 0x21, 0x21] : [255, 255, 255];
  const bg = blendOver(base, surface, alpha);
  if (contrastRatio(base, bg) >= 4.6) return rgbToHex(base);
  const target: [number, number, number] = theme === "dark" ? [255, 255, 255] : [0, 0, 0];
  for (let t = 0; t <= 1.0001; t += 0.02) {
    const candidate: [number, number, number] = [0, 1, 2].map((i) =>
      Math.round(base[i]! + (target[i]! - base[i]!) * t),
    ) as [number, number, number];
    if (contrastRatio(candidate, bg) >= 4.6) return rgbToHex(candidate);
  }
  return rgbToHex(target);
}

/**
 * Indice deterministico no estilo do Telegram (peer color): o mesmo usuario
 * recebe sempre a mesma cor da paleta `--tg-sender-*`.
 */
export function senderColorVar(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return `var(--tg-sender-${(hash % 7) + 1})`;
}
